import { useDispatch, useSelector } from "react-redux";
import { RootDispatch, RootState } from "../../../store";
import {
  clearSelectedRequestItems,
  getFloorRequestById,
  updateFloorRequestItems,
} from "../../../store/slices/Factory/newFloorSlice";
import { useEffect, useState } from "react";
import { FloorRequestStatus, IFloorRequest } from "../../../models/floor.model";
import { formatToDDMMYYYY } from "../../../utils/formatDateUtils";
import { usePreventWheelChange } from "../../../utils/usePreventWheelChange";
import toast from "react-hot-toast";
import Select from "react-select";
import {
  getDefaultUnitForType,
  getUnitsByType,
} from "../../../utils/warehouse.util";
import { UNITS } from "../../../models/warehouseRawMaterial.model";
import Button, { ButtonType } from "../../../assets/Button";

interface FloorBatchEditDetailsModalProps {
  onClose: (success: boolean) => void;
  batch?: IFloorRequest;
}

// Interface for local item management tracking removals
interface IEditableItem {
  _id: string;
  rawMaterialId?: { name: string };
  requestedQuantity: number;
  fulfilledQuantity: number;
  unit: string;
  status: FloorRequestStatus;
}

interface INewMaterialItem {
  tempId: string; // unique ID for client-side lists
  rawMaterialId: string;
  name: string;
  requestedQuantity: number;
  unit: string;
}

export default function FloorBatchEditDetailsModal({
  onClose,
  batch,
}: FloorBatchEditDetailsModalProps) {
  const dispatch: RootDispatch = useDispatch();
  const handleWheelPrevent = usePreventWheelChange();

  const items = useSelector(
    (state: RootState) => state.newFloor.selectedRequestitems,
  );
  const loading = useSelector(
    (state: RootState) => state.newFloor.loading.getFloorRequestByIdLoading,
  );
  const { rawMaterials } = useSelector((state: RootState) => state.storeInward);
  const { batchNo, userDetails: requestedBy, createdAt } = batch!;

  // Local state for existing items from Redux
  const [localItems, setLocalItems] = useState<IEditableItem[]>([]);

  // Local state for newly added items
  const [newAddedItems, setNewAddedItems] = useState<INewMaterialItem[]>([]);

  const saving = useSelector(
    (state: RootState) => state.newFloor.loading.updateFloorRequestItemsLoading,
  );
  const [materialPick, setMaterialPick] = useState({
    id: "",
    quantity: 1,
    unit: "",
  });

  const selectStyles = {
    control: (base: any) => ({
      ...base,
      minHeight: "42px",
      fontSize: "0.9rem",
      borderRadius: "0.5rem",
      borderColor: "#D1D5DB",
    }),
    valueContainer: (base: any) => ({
      ...base,
      padding: "0 12px",
    }),
    menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
  };

  useEffect(() => {
    dispatch(getFloorRequestById({ id: batch!._id }));
  }, [dispatch, batch]);

  // Sync redux store items to local state on initial load
  useEffect(() => {
    if (items) {
      setLocalItems(
        items.map((item) => ({
          _id: item._id,
          rawMaterialId: item.rawMaterialId,
          requestedQuantity: item.requestedQuantity,
          fulfilledQuantity: item.fulfilledQuantity || 0,
          unit: item.unit,
          status: item.status,
        })),
      );
    }
  }, [items]);

  const addMaterial = () => {
    if (!materialPick.id) {
      toast.error("Please select a raw material.");
      return;
    }
    if (materialPick.quantity <= 0) {
      toast.error("Quantity must be greater than zero.");
      return;
    }

    const selectedMaterial = rawMaterials.find(
      (rm) => rm._id === materialPick.id,
    );
    if (!selectedMaterial) return;

    const newItem: INewMaterialItem = {
      tempId: crypto.randomUUID(),
      rawMaterialId: selectedMaterial._id,
      name: selectedMaterial.name,
      requestedQuantity: materialPick.quantity,
      unit: materialPick.unit,
    };

    setNewAddedItems((prev) => [...prev, newItem]);

    // Reset pick picker
    setMaterialPick({
      id: "",
      quantity: 1,
      unit: "",
    });
  };

  const removeNewItem = (tempId: string) => {
    setNewAddedItems((prev) => prev.filter((item) => item.tempId !== tempId));
  };

  const updateNewItemQuantity = (tempId: string, quantity: number) => {
    setNewAddedItems((prev) =>
      prev.map((item) =>
        item.tempId === tempId
          ? { ...item, requestedQuantity: quantity }
          : item,
      ),
    );
  };

  // Save changes and compile active payload array
  const handleSaveChanges = async () => {
    if (newAddedItems.length === 0) {
      toast.error("No modifications detected to save.");
      return;
    }

    // Map newly added materials matching your API structure requirements
    const payload = newAddedItems.map((item) => ({
      rawMaterialId: item.rawMaterialId,
      requestedQuantity: item.requestedQuantity,
      unit: item.unit,
    }));

    try {
      const res = await dispatch(
        updateFloorRequestItems({ id: batch!._id, data: payload }),
      ).unwrap();
      onClose(res.success || false);
    } catch (err: any) {
      console.error(err);
    }
  };

  // Visible items matching workspace requirements
  const activeItems = localItems;

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/60 p-2 sm:p-4 backdrop-blur-xs">
      <div className="flex max-h-[95vh] w-full max-w-5xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl border border-gray-100">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-3 sm:px-6 sm:py-4 bg-gray-50/50">
          <div>
            <h2 className="text-lg font-bold text-gray-900 tracking-tight">
              Floor Batch Items
            </h2>
          </div>
          <button
            onClick={() => {
              dispatch(clearSelectedRequestItems());
              onClose(false);
            }}
            className="flex h-8 w-8 items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors text-xl font-medium"
          >
            &times;
          </button>
        </div>

        {/* Summary Info Strip */}
        <div className="grid grid-cols-2 gap-3 border-b border-gray-100 p-4 sm:p-5 sm:grid-cols-3 bg-white">
          <div className="bg-gray-50 p-2.5 rounded-lg border border-gray-100">
            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
              Batch No
            </p>
            <p className="font-semibold text-gray-800 text-sm sm:text-base mt-0.5">
              {batchNo}
            </p>
          </div>
          <div className="bg-gray-50 p-2.5 rounded-lg border border-gray-100">
            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
              Assigned to
            </p>
            <p className="font-semibold text-gray-800 text-sm sm:text-base mt-0.5">
              {requestedBy?.username}
            </p>
          </div>
          <div className="bg-gray-50 p-2.5 rounded-lg border border-gray-100 col-span-2 sm:col-span-1">
            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
              Requested Date
            </p>
            <p className="font-semibold text-gray-800 text-sm sm:text-base mt-0.5">
              {formatToDDMMYYYY(createdAt)}
            </p>
          </div>
        </div>

        {/* Add Material Section */}
        <div className="p-4 bg-green-50/50 rounded-xl border-b border-green-100">
          <label className="block text-[10px] font-bold text-green-800 mb-3 uppercase tracking-widest">
            Add Extra Raw Materials to the batch
          </label>
          <div className="flex flex-col md:flex-row gap-4 items-end">
            <div className="flex-[2] w-full">
              <Select
                options={rawMaterials.map((rm) => ({
                  value: rm._id,
                  label: rm.name,
                  unitType: rm.unitType,
                }))}
                onChange={(opt: any) => {
                  setMaterialPick({
                    id: opt?.value || "",
                    quantity: 1,
                    unit: opt ? getDefaultUnitForType(opt.unitType) : "",
                  });
                }}
                value={
                  rawMaterials.find((m) => m._id === materialPick.id)
                    ? {
                        value: materialPick.id,
                        label: rawMaterials.find(
                          (m) => m._id === materialPick.id,
                        )?.name,
                      }
                    : null
                }
                styles={selectStyles}
                menuPortalTarget={document.body}
                placeholder="Select Material"
              />
            </div>
            <div className="flex gap-3 w-full md:w-auto">
              <div className="w-24 shrink-0">
                <input
                  type="number"
                  min={1}
                  onWheel={handleWheelPrevent}
                  value={materialPick.quantity}
                  onChange={(e) =>
                    setMaterialPick((p) => ({
                      ...p,
                      quantity: +e.target.value,
                    }))
                  }
                  className="w-full text-sm px-3 py-2 border border-gray-300 rounded-lg h-[42px] outline-none"
                />
              </div>
              <div className="flex-1 min-w-[160px]">
                <Select
                  options={
                    materialPick.id
                      ? getUnitsByType(
                          rawMaterials.find((m) => m._id === materialPick.id)
                            ?.unitType || "",
                        )
                      : UNITS
                  }
                  value={
                    UNITS.find((u) => u.value === materialPick.unit) || null
                  }
                  onChange={(opt: any) =>
                    setMaterialPick((p) => ({ ...p, unit: opt?.value || "" }))
                  }
                  styles={selectStyles}
                  menuPortalTarget={document.body}
                  placeholder="Unit"
                />
              </div>
            </div>
            <Button
              type={ButtonType.Submit}
              onClick={addMaterial}
              className="text-xs h-[42px] px-8 w-full md:w-auto shrink-0"
            >
              Add
            </Button>
          </div>

          {/* Newly added items display UI */}
          {newAddedItems.length > 0 && (
            <div className="mt-4 pt-4 border-t border-green-200">
              <h4 className="text-xs font-bold text-green-900 mb-2 uppercase tracking-wide">
                New Materials to Add (Editable)
              </h4>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-left text-xs bg-white rounded-lg overflow-hidden border border-green-200">
                  <thead className="bg-green-100 text-green-800">
                    <tr>
                      <th className="px-3 py-2 font-semibold">Material</th>
                      <th className="px-3 py-2 text-center font-semibold w-24">
                        Qty (Editable)
                      </th>
                      <th className="px-3 py-2 text-center font-semibold w-20">
                        Unit
                      </th>
                      <th className="px-3 py-2 text-right font-semibold w-16">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-green-50">
                    {newAddedItems.map((item) => (
                      <tr key={item.tempId} className="hover:bg-green-50/50">
                        <td className="px-3 py-2 font-medium text-gray-800">
                          {item.name}
                        </td>
                        <td className="px-3 py-2 text-center">
                          <input
                            type="number"
                            min={1}
                            onWheel={handleWheelPrevent}
                            value={item.requestedQuantity}
                            onChange={(e) =>
                              updateNewItemQuantity(
                                item.tempId,
                                +e.target.value,
                              )
                            }
                            className="w-full text-center text-xs px-2 py-1 border border-green-300 rounded outline-none bg-green-50 focus:bg-white"
                          />
                        </td>
                        <td className="px-3 py-2 text-center text-gray-600">
                          {item.unit}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <button
                            onClick={() => removeNewItem(item.tempId)}
                            className="text-red-500 hover:text-red-700 font-semibold cursor-pointer text-xs"
                          >
                            Remove
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-auto bg-gray-50/30">
          {loading ? (
            <div className="flex justify-center items-center py-12 text-gray-500 text-sm">
              Loading details...
            </div>
          ) : activeItems.length === 0 ? (
            <div className="py-12 text-center text-sm text-gray-400 font-medium bg-white border-b">
              No items found.
            </div>
          ) : (
            <>
              {/* Desktop View */}
              <table className="hidden md:table w-full border-collapse text-left text-sm bg-white">
                <thead className="sticky top-0 bg-gray-100 text-gray-600 font-medium border-b border-gray-200 shadow-xs z-10">
                  <tr>
                    <th className="px-4 py-3 font-semibold">Material</th>
                    <th className="px-3 py-3 text-right font-semibold">
                      Fulfilled Qty
                    </th>
                    <th className="px-3 py-3 text-center font-semibold">
                      Unit
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {activeItems.map((item) => (
                    <tr
                      key={item._id}
                      className={`transition-colors hover:bg-gray-50/70`}
                    >
                      <td className="px-3 py-3 font-medium text-gray-900">
                        {item.rawMaterialId?.name}
                      </td>
                      <td className="px-3 py-3 text-right text-gray-600 font-semibold">
                        {item.fulfilledQuantity}
                      </td>
                      <td className="px-3 py-3 text-center text-gray-500 font-medium">
                        {item.unit}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Mobile Card View */}
              <div className="block md:hidden p-3 space-y-3">
                {activeItems.map((item) => (
                  <div
                    key={item._id}
                    className={`rounded-xl border p-3.5 shadow-xs space-y-3 transition-all border-gray-200 bg-white`}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="font-bold text-gray-900 text-sm leading-tight">
                          {item.rawMaterialId?.name}
                        </h4>
                        <span className="text-[11px] font-medium text-gray-500 mt-1 inline-block bg-gray-100 rounded px-1.5 py-0.2">
                          Unit: {item.unit}
                        </span>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4 pt-2 border-t border-gray-100 items-center">
                      <div>
                        <span className="block text-[10px] font-medium text-gray-400 uppercase">
                          Fulfilled Qty
                        </span>
                        <span className="text-xs font-bold text-gray-700">
                          {item.fulfilledQuantity}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Action Footer */}
        <div className="border-t border-gray-100 bg-gray-50 p-4 flex flex-col sm:flex-row items-center justify-between gap-3 shrink-0">
          <p className="text-xs text-gray-500 font-medium text-center sm:text-left">
            Review your adjustments before committing changes.
          </p>
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <button
              onClick={() => onClose(false)}
              className="w-1/2 sm:w-auto px-5 py-2 rounded-lg border border-gray-300 text-gray-700 text-xs sm:text-sm font-semibold hover:bg-gray-100 transition-all cursor-pointer"
            >
              Cancel
            </button>
            {/* Save changes only displays when there are added materials to save */}
            {newAddedItems.length > 0 && (
              <button
                onClick={handleSaveChanges}
                disabled={loading || saving}
                className="w-1/2 sm:w-auto px-5 py-2 rounded-lg bg-blue-600 text-white text-xs sm:text-sm font-semibold tracking-wide hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed shadow-xs transition-all cursor-pointer"
              >
                {saving ? "Saving..." : "Save Changes"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
